import React, { useState, useEffect, useRef } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useAuth } from '../../context/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml, sanitizeUrl, validateEmail, sanitizeObject } from '../../security/sanitize';
import { getBrandLogoPlaceholder, getCoverPlaceholder } from '../../utils/placeholders';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const Profile = () => {
  const { currentUser, userProfile, fetchUserProfile } = useAuth();
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  
  const [formData, setFormData] = useState({
    companyName: userProfile?.companyName || '',
    industry: userProfile?.industry || '',
    website: userProfile?.website || '',
    email: userProfile?.email || currentUser?.email || '',
    phone: userProfile?.phone || '',
    address: userProfile?.address || '',
    description: userProfile?.description || '',
    logo: userProfile?.logo || '/placeholder-logo.png',
    coverImage: userProfile?.coverImage || '/placeholder-cover.png',
    socialMedia: userProfile?.socialMedia || {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    },
    brandGuidelines: userProfile?.brandGuidelines || '',
    // Business details
    businessDetails: userProfile?.businessDetails || {
      registrationNumber: '',
      taxIdNumber: '',
      yearEstablished: '',
      legalEntityType: '',
      businessScope: ''
    },
    businessRegistrationDoc: userProfile?.businessRegistrationDoc || null,
    // Store Management
    stores: userProfile?.stores || []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('profile');
  const [isUploading, setIsUploading] = useState(false);
  
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [name]: value
      }
    }));
  };

  // Store management handlers
  const handleAddStore = () => {
    const newStore = {
      id: Date.now().toString(),
      name: '',
      region: '',
      city: '',
      address: '',
      active: true
    };
    setFormData(prev => ({
      ...prev,
      stores: [...prev.stores, newStore]
    }));
  };

  const handleStoreChange = (storeId, field, value) => {
    setFormData(prev => ({
      ...prev,
      stores: prev.stores.map(store => 
        store.id === storeId ? { ...store, [field]: value } : store
      )
    }));
  };

  const handleRemoveStore = (storeId) => {
    setFormData(prev => ({
      ...prev,
      stores: prev.stores.filter(store => store.id !== storeId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) return;
    
    // Get CSRF token from the page
    const csrfTokenElement = document.querySelector('input[name="csrfToken"]');
    const csrfToken = csrfTokenElement ? csrfTokenElement.value : null;
    const storedToken = sessionStorage.getItem('csrfToken');
    
    // Validate CSRF token - allow if either token is missing (for backward compatibility) or tokens match
    if (csrfToken && storedToken && csrfToken !== storedToken) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Validate business details and document if this is a new account or first-time setup
    const isNewAccount = !userProfile?.businessRegistrationDoc;
    if (isNewAccount) {
      // Check if business details are filled
      const requiredFields = ['registrationNumber', 'taxIdNumber', 'yearEstablished', 'legalEntityType'];
      const missingFields = requiredFields.filter(field => !formData.businessDetails[field]);
      
      if (missingFields.length > 0) {
        showError('Missing business details.', `Please fill in: ${missingFields.join(', ')}.`, 6000);
        return;
      }
      
      // Check if business registration document is uploaded
      if (!formData.businessRegistrationDoc) {
        showError('Please upload your business registration document.', 'This is required for account verification.', 6000);
        return;
      }
    }
    
    try {
      // Sanitize all input data
      const sanitizedData = {
        companyName: sanitizeText(formData.companyName),
        industry: sanitizeText(formData.industry),
        website: sanitizeUrl(formData.website),
        email: validateEmail(formData.email),
        phone: sanitizeText(formData.phone),
        address: sanitizeText(formData.address),
        description: sanitizeHtml(formData.description),
        logo: sanitizeUrl(formData.logo),
        coverImage: sanitizeUrl(formData.coverImage),
        socialMedia: {
          facebook: sanitizeUrl(formData.socialMedia.facebook),
          instagram: sanitizeUrl(formData.socialMedia.instagram),
          twitter: sanitizeUrl(formData.socialMedia.twitter),
          linkedin: sanitizeUrl(formData.socialMedia.linkedin)
        },
        brandGuidelines: sanitizeHtml(formData.brandGuidelines),
        // Add business details
        businessDetails: {
          registrationNumber: sanitizeText(formData.businessDetails.registrationNumber),
          taxIdNumber: sanitizeText(formData.businessDetails.taxIdNumber),
          yearEstablished: sanitizeText(formData.businessDetails.yearEstablished),
          legalEntityType: sanitizeText(formData.businessDetails.legalEntityType),
          businessScope: sanitizeText(formData.businessDetails.businessScope)
        },
        // Only include businessRegistrationDoc if it exists and hasn't been uploaded separately
        ...(formData.businessRegistrationDoc && !formData.businessRegistrationDoc.uploadedAt && {
          businessRegistrationDoc: formData.businessRegistrationDoc
        }),
        // Store locations
        stores: formData.stores.map(store => ({
          id: sanitizeText(store.id),
          name: sanitizeText(store.name),
          region: sanitizeText(store.region),
          city: sanitizeText(store.city),
          address: sanitizeText(store.address),
          active: Boolean(store.active)
        })),
        updatedAt: new Date()
      };
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, sanitizedData);
      
      fetchUserProfile(); // Refresh user profile data
      setIsEditing(false);
      
      // Show detailed success message
      const updatedSections = [];
      if (formData.companyName !== (userProfile?.companyName || '')) updatedSections.push('company info');
      if (formData.description !== (userProfile?.description || '')) updatedSections.push('description');
      if (formData.logo !== (userProfile?.logo || '')) updatedSections.push('logo');
      if (formData.coverImage !== (userProfile?.coverImage || '')) updatedSections.push('cover image');
      if (JSON.stringify(formData.socialMedia) !== JSON.stringify(userProfile?.socialMedia || {})) updatedSections.push('social media');
      if (JSON.stringify(formData.businessDetails) !== JSON.stringify(userProfile?.businessDetails || {})) updatedSections.push('business details');
      if (formData.stores.length !== (userProfile?.stores?.length || 0)) updatedSections.push('store locations');
      
      let successMessage = "Brand profile updated successfully!";
      if (updatedSections.length > 0) {
        successMessage += ` Updated sections: ${updatedSections.join(', ')}.`;
      }
      if (isNewAccount) {
        successMessage += " Your account is now ready for campaign creation!";
      }
      
      showSuccess(successMessage, "Your brand profile has been updated successfully.", 6000);
    } catch (error) {
      console.error("Error updating brand profile:", error);
      
      let errorMsg = "Failed to update brand profile.";
      let errorDetails = "";
      
      // Provide specific error messages based on error type
      if (error.code === 'permission-denied') {
        errorMsg = "Permission denied while updating profile.";
        errorDetails = "Please verify your account permissions or contact support.";
      } else if (error.code === 'unavailable') {
        errorMsg = "Service temporarily unavailable.";
        errorDetails = "Our servers are experiencing issues. Please try again in a few minutes.";
      } else if (error.code === 'unauthenticated') {
        errorMsg = "Authentication required.";
        errorDetails = "Your session has expired. Please log out and log back in.";
      } else if (error.code === 'storage/unauthorized') {
        errorMsg = "Document upload failed.";
        errorDetails = "Unable to upload business documents. Please check file size and format.";
      } else if (error.code === 'storage/quota-exceeded') {
        errorMsg = "Storage limit exceeded.";
        errorDetails = "Your account storage is full. Please contact support to increase your limit.";
      } else if (error.message && error.message.includes('network')) {
        errorMsg = "Network connection error.";
        errorDetails = "Please check your internet connection and try again.";
      } else if (error.message && error.message.includes('validation')) {
        errorMsg = "Validation error.";
        errorDetails = "Please check all required fields and ensure data is properly formatted.";
      } else if (error.message) {
        errorMsg = `Update failed: ${error.message}`;
        errorDetails = "If this problem persists, please contact our support team.";
      } else {
        errorDetails = "Please try again or contact support if the issue continues.";
      }
      
      const fullErrorMsg = errorDetails ? `${errorMsg} ${errorDetails}` : errorMsg;
      showError('Failed to update profile.', fullErrorMsg, 8000);
    }  
  };
  
  const handleFileUpload = async (file, type) => {
    if (!file || !currentUser) return null;
    
    setIsUploading(true);
    try {

      
      // Create a local URL for preview while uploading
      const localUrl = URL.createObjectURL(file);
      
      if (type === 'businessRegistrationDoc') {
        setFormData(prev => ({
          ...prev,
          businessRegistrationDoc: {
            name: file.name,
            localUrl: localUrl,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [type === 'logo' ? 'logo' : 'coverImage']: localUrl
        }));
      }
      
      // Upload to Firebase Storage using the appropriate path
      let path;
      if (type === 'logo') {
        path = 'profileImages';
      } else if (type === 'coverImage') {
        path = 'portfolio';
      } else if (type === 'businessRegistrationDoc') {
        path = 'businessDocuments';
      }
      
      // Create a safe filename by removing special characters
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const timestamp = Date.now();
      const fileRef = ref(storage, `${path}/${currentUser.uid}/${timestamp}_${safeFileName}`);
      

      
      // Upload the file
      const uploadTask = await uploadBytes(fileRef, file);

      
      // Get the download URL
      const downloadUrl = await getDownloadURL(fileRef);

      
      // Update with the permanent URL
      if (type === 'businessRegistrationDoc') {
        const docData = {
          name: file.name,
          url: downloadUrl,
          size: file.size,
          type: file.type,
          uploadedAt: new Date()
        };
        
        setFormData(prev => ({
          ...prev,
          businessRegistrationDoc: docData
        }));
        
        // Save the document URL to Firestore immediately
        const userDocRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userDocRef, {
          businessRegistrationDoc: docData,
          updatedAt: new Date()
        });
        

      } else {
        setFormData(prev => ({
          ...prev,
          [type === 'logo' ? 'logo' : 'coverImage']: downloadUrl
        }));
        
        // Save the image URL to Firestore immediately
        const userDocRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userDocRef, {
          [type === 'logo' ? 'logo' : 'coverImage']: downloadUrl,
          updatedAt: new Date()
        });
        

      }
      
      // Refresh user profile data to ensure it's updated
      fetchUserProfile();
      
      return downloadUrl;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      showError(`Failed to upload ${type}.`, 'Please check your file and try again.', 6000);
      return null;
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, 'logo');
    }
  };
  
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, 'coverImage');
    }
  };
  
  const handleBusinessDocChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        handleFileUpload(file, 'businessRegistrationDoc');
      } catch (error) {
        console.error('Error uploading business document:', error);
        showError('Failed to upload business document.', 'Please check your file and try again.', 6000);
      }
    }
  };
  
  const handleBusinessDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      businessDetails: {
        ...prev.businessDetails,
        [name]: value
      }
    }));
  };

  return (
    <div>
      {/* Toast Notifications */}

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Brand Profile</h2>
        <p className="text-gray-600">Manage your company information and brand guidelines</p>
      </div>

      {/* Profile Header with Cover Image */}
      <div className="bg-white rounded-lg shadow mb-8 overflow-hidden">
        <div className="h-48 bg-gray-200 relative">
          <img 
            src={formData.coverImage || getCoverPlaceholder()} 
            alt="Cover" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = getCoverPlaceholder();
            }}
          />
          {isEditing && (
            <>
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverChange}
                accept="image/*"
                className="hidden"
              />
              <button 
                onClick={() => coverInputRef.current?.click()}
                disabled={isUploading}
                className="absolute bottom-4 right-4 bg-white rounded-md shadow px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Change Cover'}
              </button>
            </>
          )}
        </div>
        
        <div className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center">
          <div className="w-24 h-24 rounded-lg bg-gray-200 -mt-12 border-4 border-white shadow-sm relative">
            <img 
              src={formData.logo || getBrandLogoPlaceholder(formData.companyName)} 
              alt="Logo" 
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = getBrandLogoPlaceholder(formData.companyName);
              }}
            />
            {isEditing && (
              <>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleLogoChange}
                  accept="image/*"
                  className="hidden"
                />
                <button 
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute bottom-0 right-0 bg-white rounded-full shadow p-1 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </>
            )}
          </div>
          
          <div className="mt-4 sm:mt-0 sm:ml-6 flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{formData.companyName}</h1>
            <p className="text-gray-600">{formData.industry}</p>
          </div>
          
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="mt-4 sm:mt-0 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Edit Profile
            </button>
          ) : (
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
        
        <div className="border-t border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setSelectedTab('profile')}
              className={`px-6 py-3 text-sm font-medium ${
                selectedTab === 'profile'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setSelectedTab('business')}
              className={`px-6 py-3 text-sm font-medium ${
                selectedTab === 'business'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Business Details
            </button>
            <button
              onClick={() => setSelectedTab('guidelines')}
              className={`px-6 py-3 text-sm font-medium ${
                selectedTab === 'guidelines'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Brand Guidelines
            </button>
            <button
              onClick={() => setSelectedTab('stores')}
              className={`px-6 py-3 text-sm font-medium ${
                selectedTab === 'stores'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Store Locations
            </button>
          </nav>
        </div>
      </div>

      {selectedTab === 'profile' ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Company Information</h3>
          </div>
          <div className="px-6 py-5">
            <form onSubmit={handleSubmit}>
              <CSRFToken />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    id="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700">Industry</label>
                  <input
                    type="text"
                    name="industry"
                    id="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website</label>
                  <input
                    type="url"
                    name="website"
                    id="website"
                    value={formData.website}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Company Description</label>
                <textarea
                  name="description"
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Social Media</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="facebook" className="block text-sm font-medium text-gray-500">Facebook</label>
                    <input
                      type="url"
                      name="facebook"
                      id="facebook"
                      value={formData.socialMedia.facebook}
                      onChange={handleSocialMediaChange}
                      disabled={!isEditing}
                      className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="instagram" className="block text-sm font-medium text-gray-500">Instagram</label>
                    <input
                      type="url"
                      name="instagram"
                      id="instagram"
                      value={formData.socialMedia.instagram}
                      onChange={handleSocialMediaChange}
                      disabled={!isEditing}
                      className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="twitter" className="block text-sm font-medium text-gray-500">Twitter</label>
                    <input
                      type="url"
                      name="twitter"
                      id="twitter"
                      value={formData.socialMedia.twitter}
                      onChange={handleSocialMediaChange}
                      disabled={!isEditing}
                      className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="linkedin" className="block text-sm font-medium text-gray-500">LinkedIn</label>
                    <input
                      type="url"
                      name="linkedin"
                      id="linkedin"
                      value={formData.socialMedia.linkedin}
                      onChange={handleSocialMediaChange}
                      disabled={!isEditing}
                      className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : selectedTab === 'business' ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Business Details</h3>
            <p className="mt-1 text-sm text-gray-500">
              Provide your official business information and registration documents
              {!userProfile?.businessRegistrationDoc && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Required
                </span>
              )}
            </p>
          </div>
          <div className="px-6 py-5">
            <form onSubmit={handleSubmit}>
              <CSRFToken />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700">
                    Business Registration Number
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="registrationNumber"
                    id="registrationNumber"
                    value={formData.businessDetails.registrationNumber}
                    onChange={handleBusinessDetailsChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="taxIdNumber" className="block text-sm font-medium text-gray-700">
                    Tax ID Number
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="taxIdNumber"
                    id="taxIdNumber"
                    value={formData.businessDetails.taxIdNumber}
                    onChange={handleBusinessDetailsChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="yearEstablished" className="block text-sm font-medium text-gray-700">
                    Year Established
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="yearEstablished"
                    id="yearEstablished"
                    value={formData.businessDetails.yearEstablished}
                    onChange={handleBusinessDetailsChange}
                    disabled={!isEditing}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="legalEntityType" className="block text-sm font-medium text-gray-700">
                    Legal Entity Type
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.businessDetails.legalEntityType}
                    onValueChange={(value) => handleBusinessDetailsChange({ target: { name: 'legalEntityType', value } })}
                    disabled={!isEditing}
                  >
                    <SelectTrigger id="legalEntityType" className="mt-1 w-full">
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Corporation">Corporation</SelectItem>
                      <SelectItem value="Partnership">Partnership</SelectItem>
                      <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                      <SelectItem value="LLC">LLC</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="mt-6">
                <label htmlFor="businessScope" className="block text-sm font-medium text-gray-700">Business Scope</label>
                <textarea
                  name="businessScope"
                  id="businessScope"
                  rows={3}
                  value={formData.businessDetails.businessScope}
                  onChange={handleBusinessDetailsChange}
                  disabled={!isEditing}
                  className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Briefly describe your business scope and operations..."
                />
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700">
                  Business Registration Document
                  <span className="ml-1 text-red-500">*</span>
                </label>
                
                {formData.businessRegistrationDoc ? (
                  <div className="mt-2 flex items-center p-4 border border-gray-200 rounded-md">
                    <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="ml-4 flex-1 truncate">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formData.businessRegistrationDoc.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formData.businessRegistrationDoc.uploadedAt ? 
                          `Uploaded on ${new Date(formData.businessRegistrationDoc.uploadedAt?.seconds * 1000).toLocaleDateString()}` : 
                          'Document uploaded'}
                      </div>
                    </div>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => {
                          const businessDocInput = document.getElementById('businessDocInput');
                          if (businessDocInput) businessDocInput.click();
                        }}
                        className="ml-4 py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Replace
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    {isEditing ? (
                      <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="flex text-sm text-gray-600">
                            <label htmlFor="businessDocInput" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                              <span>Upload document</span>
                              <input 
                                id="businessDocInput" 
                                name="businessDocInput" 
                                type="file" 
                                className="sr-only" 
                                onChange={handleBusinessDocChange}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-500">
                            PDF, DOC, DOCX, JPG, JPEG, PNG up to 10MB
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-yellow-50 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">No document uploaded</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>Click "Edit Profile" to upload your business registration document.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  Please upload an official business registration document issued by a government agency.
                </p>
              </div>
            </form>
          </div>
        </div>
      ) : selectedTab === 'guidelines' ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Brand Guidelines</h3>
            <p className="mt-1 text-sm text-gray-500">
              Define your brand voice, style, and requirements for influencers
            </p>
          </div>
          <div className="px-6 py-5">
            <form onSubmit={handleSubmit}>
              <CSRFToken />
              <div>
                <label htmlFor="brandGuidelines" className="block text-sm font-medium text-gray-700">Brand Voice & Style</label>
                <textarea
                  name="brandGuidelines"
                  id="brandGuidelines"
                  rows={6}
                  value={formData.brandGuidelines}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Describe your brand voice, style, and key messaging points..."
                />
              </div>
              
              {isEditing && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700">Brand Assets</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                          <span>Upload brand assets</span>
                          <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Content Requirements</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="product_shots"
                        name="product_shots"
                        type="checkbox"
                        defaultChecked={true}
                        disabled={!isEditing}
                        className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="product_shots" className="font-medium text-gray-700">Product Shots</label>
                      <p className="text-gray-500">Require influencers to include clear product shots in their content</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="brand_mention"
                        name="brand_mention"
                        type="checkbox"
                        defaultChecked={true}
                        disabled={!isEditing}
                        className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="brand_mention" className="font-medium text-gray-700">Brand Mention</label>
                      <p className="text-gray-500">Require influencers to mention your brand name in their content</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="hashtags"
                        name="hashtags"
                        type="checkbox"
                        defaultChecked={true}
                        disabled={!isEditing}
                        className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="hashtags" className="font-medium text-gray-700">Campaign Hashtags</label>
                      <p className="text-gray-500">Require influencers to use specific campaign hashtags</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="honest_review"
                        name="honest_review"
                        type="checkbox"
                        defaultChecked={true}
                        disabled={!isEditing}
                        className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="honest_review" className="font-medium text-gray-700">Honest Reviews</label>
                      <p className="text-gray-500">Allow influencers to share honest opinions about your products</p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : selectedTab === 'stores' ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Store Locations</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your store locations for campaign targeting
                </p>
                {/* Debug info */}
                <div className="mt-2 text-xs text-blue-600">
                  Debug: isEditing = {isEditing ? 'true' : 'false'}, stores count = {formData.stores ? formData.stores.length : 'undefined'}
                </div>
              </div>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleAddStore}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Store
                </button>
              )}
            </div>
          </div>
          <div className="px-6 py-5">
            {formData.stores.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No stores added</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {isEditing ? 'Add your first store location to get started.' : 'Click "Edit Profile" to add store locations.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.stores.map((store, index) => (
                  <div key={store.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-sm font-medium text-gray-900">
                        Store {index + 1}
                      </h4>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => handleRemoveStore(store.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Store Name</label>
                        <input
                          type="text"
                          value={store.name}
                          onChange={(e) => handleStoreChange(store.id, 'name', e.target.value)}
                          disabled={!isEditing}
                          placeholder="e.g. SM Mall of Asia"
                          className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Region</label>
                        <input
                          type="text"
                          value={store.region}
                          onChange={(e) => handleStoreChange(store.id, 'region', e.target.value)}
                          disabled={!isEditing}
                          placeholder="e.g. NCR, Cebu, Davao"
                          className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">City</label>
                        <input
                          type="text"
                          value={store.city}
                          onChange={(e) => handleStoreChange(store.id, 'city', e.target.value)}
                          disabled={!isEditing}
                          placeholder="e.g. Manila, Makati, Quezon City"
                          className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`active-${store.id}`}
                          checked={store.active}
                          onChange={(e) => handleStoreChange(store.id, 'active', e.target.checked)}
                          disabled={!isEditing}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`active-${store.id}`} className="ml-2 block text-sm text-gray-700">
                          Active for campaigns
                        </label>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <textarea
                          value={store.address}
                          onChange={(e) => handleStoreChange(store.id, 'address', e.target.value)}
                          disabled={!isEditing}
                          rows={2}
                          placeholder="Full store address"
                          className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5">
            <p className="text-gray-500">Select a tab to view content.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
